/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ShowMoreGridSection } from '@coursera-web/cds-labs';

import { Grid, PageGridContainer, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import MerchandisingCard from 'bundles/enterprise-collections/components/MerchandisingCard';
import { useCustomCourses } from 'bundles/enterprise-collections/context/CustomCoursesContext';
import SiteMiscExperiments from 'bundles/epic/clients/siteMisc';
import EnterpriseCustomCourseProductCard from 'bundles/page-config-common/components/enterprise/EnterpriseCustomCourseProductCard';
import EnterpriseLearningPathProductCard from 'bundles/page-config-common/components/enterprise/EnterpriseLearningPathProductCard';
import { useEnterpriseBadgeCollections } from 'bundles/page-config-common/contexts/enterprise/EnterpriseBadgeCollectionsContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useEnterpriseAllSkillPathways } from 'bundles/unified-home-common/contexts/enterprise/EnterpriseAllSkillPathwaysContext';
import type { MyLearning_SkillPathwayFragment as SkillPathway } from 'bundles/unified-home-common/queries/__generated__/EnterpriseGetAllSkillPathways';
import shouldShowSkillPathways from 'bundles/unified-home-common/utils/shouldShowSkillPathways';

import _t from 'i18n!nls/enterprise-collections';

type PropsFromCaller = {
  thirdPartyOrgName: string;
  programSlug: string;
  programName: string;
};

const styles = {
  container: css`
    max-width: 1440px;

    section {
      padding-top: 0;
      padding-bottom: 0;
    }

    margin-top: var(--cds-spacing-200);

    ${breakpoints.down('md')} {
      padding: 0 var(--cds-spacing-200);
    }
    ${breakpoints.up('md')} {
      padding: 0 var(--cds-spacing-600);
    }
    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-50);
      padding-top: var(--cds-spacing-300);
      margin-top: calc(var(--cds-spacing-50) * -1);
    }
  `,

  targetCardWrapper: css`
    height: 100%;
  `,

  targetCardWrapperCtrl: css`
    .cds-ProductCard-list {
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,

  listCardContainer: css`
    /* intentionally unset min-height for list card that was default from CDS */
    min-height: unset;

    .cds-ProductCard-list {
      height: inherit;

      /* intentionally unset min-width for list card that was default from CDS */
      min-width: unset;
    }
  `,

  cardsSection: css`
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-300);
    }

    .cds-grid-item:first-child {
      ${breakpoints.down('xs')} {
        margin-top: var(--cds-spacing-50);
      }
    }

    .cds-grid-item {
      ${breakpoints.down('xs')} {
        margin-bottom: calc(var(--cds-spacing-100) * -1);
      }
    }

    .cds-grid-item:last-child {
      ${breakpoints.down('xs')} {
        margin-bottom: var(--cds-spacing-100);
      }
    }
  `,
};

const TailoredLearningPageContent = ({ thirdPartyOrgName, programSlug, programName }: PropsFromCaller) => {
  const { enterpriseBadgesWithCollection } = useEnterpriseBadgeCollections();
  const program = useProgram();
  const allSkillPathways = useEnterpriseAllSkillPathways();
  const { totalCollections, privateCourses } = useCustomCourses();
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('sm'));
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  // Skill pathways are a special kind of learning path/badge. These relate to the items in enterpriseBadgesWithCollection and provide extra metadata.
  // We should not iterate through skillPathways, but find any matching items in enterpriseBadgesWithCollection and use skillPathway item to display extra content.
  const skillPathwayIdIndexMap = React.useMemo(() => {
    return allSkillPathways?.skillPathways?.reduce((acc, skillPathway) => {
      acc[skillPathway.badgeTemplateId] = skillPathway;
      return acc;
    }, {} as Record<string, SkillPathway>);
  }, [allSkillPathways]);
  // Filter out any learning paths that are considered skill pathways if they should not be displayed for the current program/AB test.
  const filteredEnterpriseBadgesWithCollection = enterpriseBadgesWithCollection.filter((badge) => {
    // If no matching skill pathway, it's a standard learning path, display it.
    if (!skillPathwayIdIndexMap?.[badge.enterpriseBadge.badgeTemplateId]) {
      return true;
    } else {
      // Conditionally include skill pathways based on program/AB test.
      return shouldShowSkillPathways(program?.id);
    }
  });

  if (allSkillPathways.loading) {
    return null;
  }

  let learningPathRowsToShow = 0;
  let customCoursesRowsToShow = 0;

  if (privateCourses.length > 0 && filteredEnterpriseBadgesWithCollection.length > 0) {
    learningPathRowsToShow = isMobile ? 2 : 1;
    customCoursesRowsToShow = isMobile ? 2 : 1;
  } else if (filteredEnterpriseBadgesWithCollection.length > 0) {
    learningPathRowsToShow = 4;
  } else if (privateCourses.length > 0) {
    customCoursesRowsToShow = 4;
  }

  const showMerchandisingComponent = totalCollections > 0;
  const experimentVariant = SiteMiscExperiments.preview('productCardTest1');
  const variantType = isTabletOrSmaller ? 'list' : 'grid';

  const getDescription = () => {
    if (privateCourses.length > 0 && filteredEnterpriseBadgesWithCollection.length > 0) {
      return _t(
        'Discover custom learning paths and courses to boost your career, enhance your skills, and excel in high-demand roles'
      );
    } else if (privateCourses.length > 0) {
      return _t('Discover custom courses to boost your career, enhance your skills, and excel in high-demand roles');
    } else if (filteredEnterpriseBadgesWithCollection.length > 0) {
      return _t(
        'Discover custom learning paths to boost your career, enhance your skills, and excel in high-demand roles'
      );
    } else {
      return null;
    }
  };

  return (
    <PageGridContainer css={styles.container}>
      <Grid item xs={12}>
        <Typography2 component="h1" variant="titleMedium">
          {_t('Content tailored for learning')}
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" color="supportText">
          {getDescription()}
        </Typography2>
      </Grid>
      {learningPathRowsToShow > 0 && (
        <Grid item xs={12} css={styles.cardsSection}>
          <ShowMoreGridSection
            renderTitle={() => (
              <Typography2 component="h2" variant="subtitleLarge">
                {_t('Learning paths that offer a badge')}
              </Typography2>
            )}
            defaultRows={learningPathRowsToShow}
            spacing={{ sm: 24, xs: 16 }}
            items={filteredEnterpriseBadgesWithCollection}
            renderItem={({ item: badge, itemIndex }) => (
              <EnterpriseLearningPathProductCard
                enterpriseBadgeWithCollection={badge}
                itemIndex={itemIndex}
                isSkillPathway={!!skillPathwayIdIndexMap?.[badge.id]}
              />
            )}
          />
        </Grid>
      )}
      {customCoursesRowsToShow > 0 && (
        <Grid item xs={12} css={styles.cardsSection}>
          <ShowMoreGridSection
            renderTitle={() => (
              <Typography2 component="h2" variant="subtitleLarge">
                {_t('Courses created by #{thirdPartyOrgName}', { thirdPartyOrgName })}
              </Typography2>
            )}
            defaultRows={customCoursesRowsToShow}
            spacing={{ sm: 24, xs: 16 }}
            items={privateCourses}
            renderItem={({ item: privateCourse, itemIndex }) => (
              <div
                css={[
                  styles.targetCardWrapper,
                  experimentVariant !== 'test1' && styles.targetCardWrapperCtrl,
                  variantType === 'list' ? styles.listCardContainer : null,
                ]}
              >
                <EnterpriseCustomCourseProductCard customCourse={privateCourse} itemIndex={itemIndex} />
              </div>
            )}
          />
        </Grid>
      )}
      {showMerchandisingComponent && (
        <Grid item xs={12}>
          <MerchandisingCard
            programName={programName}
            subTitle={_t('Collections selected by #{orgName}', { orgName: thirdPartyOrgName })}
            description={_t('Curated collections to elevate expertise and boost career growth')}
            redirectUrl={`/programs/${programSlug}/view-all-collections/curated`}
          />
        </Grid>
      )}
    </PageGridContainer>
  );
};

export default TailoredLearningPageContent;
